import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

/**
 * SIGM parts, as served by `/part/extended` (grid shape: group, suppliers,
 * quantities) and `/part/full/{id}` (adds purchase-order lines and
 * transactions). Wire names match the Java `@JsonProperty`s exactly.
 */

export interface PartGroupDTO {
  id: number;
  no?: string | null;
  description?: string | null;
  category_no?: number;
}

export interface PartQuantityDTO {
  id: number;
  warehouse_no?: string | null;
  location?: string | null;
  part_id?: number;
  real_qty?: number;
  reserved_orders?: number;
  reserved_purchase_lot?: number;
  reserved_production_lot?: number;
  reserved_project?: number;
  reserved_warehouse?: number;
  avg_cost?: number;
  last_cost?: number;
  min_qty?: number;
  max_qty?: number;
}

export interface SupplierDTO {
  id: number;
  no?: string | null;
  active?: boolean;
  name?: string | null;
  country?: string | null;
  /** Currency id: 0 = CAD, 1 = USD, -1 = ALL. */
  currency?: number;
}

export interface PartSupplierDTO {
  id: number;
  idx?: number | null;
  supplier_part_no?: string | null;
  supplier_unit?: string | null;
  supplier_price?: number | null;
  cost_change_date?: string | null;
  supplier?: SupplierDTO | null;
}

export interface PartWarehouseDTO {
  id: number;
  no?: string | null;
  active?: boolean;
  default?: boolean;
  name1?: string | null;
  name2?: string | null;
}

/** A purchase-order line with its header fields flattened alongside. */
export interface PurchaseOrderLineDTO {
  id: number;
  no?: number;
  user?: string | null;
  reference?: string | null;
  puh_date?: string | null;
  req_date?: string | null;
  currency?: number;
  subtotal?: number;
  tax1?: number;
  tax2?: number;
  total?: number;
  line_id?: number;
  line_part_id?: number;
  line_purchase_qty?: number;
  line_received_qty?: number;
  line_cancel_qty?: number;
  line_req_date?: string | null;
  line_init_rcvg_dt?: string | null;
  psp_price?: number;
  psp_part_no?: string | null;
  supplier?: SupplierDTO | null;
  warehouse?: PartWarehouseDTO | null;
}

export interface PartTransactionDTO {
  id: number;
  no?: number;
  type?: number;
  part_no?: string | null;
  user?: string | null;
  date?: string | null;
  timestamp?: string | null;
  ptn_adj_no?: number;
  description?: string | null;
  qty_before?: number;
  transaction_qty?: number;
  qty_after?: number;
  total_cost?: number;
  puh_no?: number;
  order_no?: number;
  project_no?: number;
  req_no?: string | null;
  warehouse_no?: string | null;
}

export interface PartDTO {
  id: number;
  no?: string | null;
  description1?: string | null;
  description2?: string | null;
  description3?: string | null;
  active?: boolean;
  type?: string | null;
  sort?: string | null;
  unit?: string | null;
  default_supplier_idx?: number;
  bundle_quantity?: number;
  part_group?: PartGroupDTO | null;
  suppliers?: PartSupplierDTO[];
  part_quantities?: PartQuantityDTO[];
  /** Only populated by `/part/full/{id}`. */
  purchase_orders?: PurchaseOrderLineDTO[];
  /** Only populated by `/part/full/{id}`. */
  transactions?: PartTransactionDTO[];
}

/** The SIGM currency ids used on parts/suppliers/purchase orders. */
export function currencyFromId(id: number | null | undefined): string {
  switch (id) {
    case 0:
      return "CAD";
    case 1:
      return "USD";
    case -1:
      return "ALL";
    default:
      return id === null || id === undefined ? "" : String(id);
  }
}

/** On-hand across all warehouses — the grid's "real qty" column. */
export function totalQuantity(part: PartDTO): number {
  return (part.part_quantities ?? []).reduce((sum, quantity) => sum + (quantity.real_qty ?? 0), 0);
}

export class PartClient extends ResourceClient<PartDTO> {
  constructor(http: HttpClient) {
    super(http, "/part");
  }

  /** Active parts (server drops groups 890/870/376) in the extended shape. */
  extendedActive(): Promise<PartDTO[]> {
    return this.http.get<PartDTO[]>("/part/extended/active");
  }

  /** One part with purchase-order lines and transactions included. */
  full(id: number): Promise<PartDTO> {
    return this.http.get<PartDTO>(`/part/full/${id}`);
  }

  groups(): Promise<PartGroupDTO[]> {
    return this.http.get<PartGroupDTO[]>("/part/group");
  }

  /** Part inventory workbook as of a date, limited to some part groups. */
  documentPartInventory(date: string, partGroupIds: number[]) {
    return this.http.job("/part/document/part/inventory", {
      query: { date },
      body: partGroupIds,
    });
  }

  /** Everything the service warehouse (SERV) held at the end of a date, biggest quantity first. */
  documentServicePartInventory(date: string) {
    return this.http.job("/part/document/service/inventory", { query: { date } });
  }

  documentProductInventory(date: string) {
    return this.http.job("/part/document/product/inventory", { query: { date } });
  }

  documentWipInventory(date: string) {
    return this.http.job("/part/document/wip/inventory", { query: { date } });
  }

  /** Accounting report for one month; the body is the Month enum name. */
  documentAccounting(year: number, month: string) {
    return this.http.job("/part/document/accounting", { query: { year }, body: month });
  }
}
